import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { formatBytes, uInt8ToBase64 } from '/imports/utils';
import type { FileMeta } from '/imports/types';
import { strings } from '/imports/strings';
import { finishFileUpload, uploadFileChunk, fileHref, uploadLimitBytes, executableUploadRefusal, startUploadMethodFor } from '/imports/api/files';
import { css } from '/imports/ui/css';
import { Icon } from '/imports/ui/Icon';
import { isDragLeaving, isFileDrag } from '/imports/ui/utils';

const FileInputStyled = styled.div`
  .wrapper { 
    display: flex;
    .meta {
      &.hide {
        display: none;
      }
    }
  }
  &.dragging .target {
    border-color: ${css.primaryColor};
    background: ${css.lightBlue};
  }
  .target {
    border-radius: ${css.inputBorderRadius};
    border: 2px dashed ${css.grey4};
    padding: 1.5rem;
    text-align: center;
    cursor: pointer;
    .icon {
      margin: auto;
      flex-direction: row;
    }
  }
  input {
    width: 100%;
    background: ${css.inputBackgroundColor};
    border: ${css.inputBorder};
    border-radius: ${css.inputBorderRadius};
    border: 2px dashed ${css.grey4};
    padding: 0.5rem 0.75rem;
    box-sizing: border-box;
    font-size: 1rem;
    font-family: ${css.fontFamily};
    display: none;
    &::placeholder {
      text-transform: capitalize;
    }
    &.hide {
      display: none;
    }
  }
`;

export interface FileInputProps {
  id?: string;
  className?: string;
  placeholder?: string;
  label?: string;            // drop-target caption (defaults to "Select File")
  value?: FileMeta;
  disabled?: boolean;
  showMeta?: boolean;
  accept?: string;
  maxSize?: number;          // byte ceiling for this surface; clamped server-side to maxUploadBytes
  onFile?(file: File): File | Promise<File>;
  onChange?(fileMeta: FileMeta, e?: React.ChangeEvent<HTMLInputElement>): void;
  onUploadStart?: (file: File) => void;
  onUploadProgress?: (progress: number, file: File) => void;
  onUploadDone?: (meta: FileMeta, file: File) => void;
}

/** File upload input with drag-and-drop, chunked upload, and progress display. */
export const FileInput = (props: FileInputProps) => {
  const { value } = props;
  const [file, setFile] = useState<File>();
  const [uploadProgress, setUploadProgress] = useState<number>();
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<any>(undefined);
  // Bound to the whole component rather than the drop target alone, so a drop lands whether the input is
  // showing its empty target or the thumbnail of a file already chosen — the same as clicking either.
  const dragHandlers = {
    onDragEnter: (e: React.DragEvent) => {
      if (props.disabled || !isFileDrag(e)) return;
      e.preventDefault();
      setDragging(true);
    },
    onDragOver: (e: React.DragEvent) => {
      if (props.disabled || !isFileDrag(e)) return;
      e.preventDefault();
    },
    onDragLeave: (e: React.DragEvent) => {
      if (isDragLeaving(e)) setDragging(false);
    },
    onDrop: async (e: React.DragEvent) => {
      if (props.disabled || !isFileDrag(e)) return;
      e.preventDefault();
      setDragging(false);
      // One file: this input holds a single value, so a multi-file drop takes the first rather than
      // silently keeping the last.
      await handleFile(Array.from(e.dataTransfer.files)[0]);
    },
  };
  /**
   * Handles file selection from the input element.
   * @param e - input change event
   */
  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = Array.from(e.target.files || [])[0];
    if (file) await handleFile(file, e);
  };
  /**
   * Uploads a file in chunks, reporting progress and firing lifecycle callbacks.
   * @param file - file to upload
   * @param e - originating change event, if any
   */
  const handleFile = async (file?: File, e?: React.ChangeEvent<HTMLInputElement>) => {
    if (!file) return;
    file = props.onFile ? await props.onFile(file) : file;
    const limit = uploadLimitBytes(props.maxSize);
    if (file.size > limit) return toast.error(strings.FileTooLarge(file.name, formatBytes(file.size), formatBytes(limit)));
    const refusal = executableUploadRefusal(file, props.accept);
    if (refusal) return toast.error(refusal);
    let fileMeta: FileMeta | undefined = undefined;
    let progress = 0;
    setFile(file);
    setUploadProgress(progress);
    const { name, type, size } = file;
    props.onUploadStart?.(file);
    const token = await startUploadMethodFor(props.accept).call({ name, type, size, maxSize: props.maxSize });
    progress = 1;
    setUploadProgress(progress);
    props.onUploadProgress?.(progress, file);
    const stream = file.stream() as any;
    const reader = stream.getReader();
    let uploadedBytes = 0;
    let read;
    let chunk;
    while ((read = await reader.read())) {
      if (read.value) {
        chunk = uInt8ToBase64(read.value);
        await uploadFileChunk.call({ token, chunk });
        uploadedBytes += read.value.byteLength;
        progress = Math.round(uploadedBytes / size * 100);
        setUploadProgress(progress);
        props.onUploadProgress?.(progress, file);
      }
      if (read.done) {
        fileMeta = await finishFileUpload.call(token);
        props.onUploadDone?.(fileMeta, file);
        setUploadProgress(undefined);
        break;
      }
    }
    fileMeta && props.onChange?.(fileMeta, e);
  };
  /**
   * Updates the file's name from the edit input and propagates the change.
   * @param fileMeta - file metadata to mutate
   * @param e - input change event
   */
  const handleNameChange = (fileMeta: FileMeta, e: React.ChangeEvent<HTMLInputElement>) => {
    fileMeta.name = e.target.value;
    props.onChange?.(fileMeta, e);
  };
  return (
    <FileInputStyled className={`${props.className || ''}${dragging ? ' dragging' : ''}`} {...dragHandlers}>
      {
        value && !uploadProgress ?
          (
            <div className="wrapper">
              <Thumb file={value} onClick={() => fileInput.current?.click()} />
              <div className={`meta${props.showMeta ? '' : ' hide'}`}>
                <div><input value={value.name} onChange={(e) => handleNameChange(value, e)} /></div>
                <div>{value.type}</div>
                <div>{formatBytes(value.size)}</div>
              </div>
            </div>
          )
          :
          null
      }
      <input
        id={props.id}
        ref={fileInput}
        className={value ? 'hide' : ''}
        type="file"
        placeholder={props.placeholder || ''}
        multiple={true}
        accept={props.accept}
        disabled={props.disabled}
        onChange={(e) => handleChange(e)}
      />
      {
        !value || uploadProgress ?
          (
            <div
              className="target"
              onClick={() => fileInput.current.click()}
              style={file && uploadProgress ? { background: `linear-gradient(to right, ${css.grey5} ${uploadProgress}%, transparent ${uploadProgress}%)` } : {}}
            >
              <Icon icon="postAdd" size={40} />
              <div>{uploadProgress ? `${strings.Uploading()}...${uploadProgress}%` : dragging ? strings.DropToUpload() : (props.label || strings.SelectFile())}</div>
            </div>
          )
          :
          null
      }
    </FileInputStyled>
  );
};

const ThumbStyled = styled.div`
  text-align: center;
  padding-right: 2rem;
  cursor: pointer;
  img, video, iframe {
    max-height: 7rem;
    max-width: 7rem;
  }
`;

interface ThumbProps {
  file: FileMeta;
  onClick?(): void;
}

/** Renders a clickable thumbnail for an image, video, PDF, or generic file. */
const Thumb = (props: ThumbProps) => {
  const file = props.file;
  if (file.type.startsWith('image')) {
    return (
      <ThumbStyled className="file-widget-thumb">
        <a onClick={() => props.onClick?.()}>
          <img src={fileHref(file)} alt={file.name} />
        </a>
      </ThumbStyled>
    );
  }
  if (file.type.startsWith('video')) {
    return (
      <ThumbStyled className="file-widget-thumb">
        <a onClick={() => props.onClick?.()}>
          <video src={fileHref(file)} />
        </a>
      </ThumbStyled>
    );
  }
  if (file.type.startsWith('application/pdf')) {
    return (
      <ThumbStyled className="file-widget-thumb">
        <a onClick={() => props.onClick?.()}>
          <iframe src={fileHref(file)} />
        </a>
      </ThumbStyled>
    );
  }
  return (
    <ThumbStyled className="file-widget-thumb">
      <a href={fileHref(file)}>{file.name}</a>
    </ThumbStyled>
  );
};